/**
 * Combobox (autocomplete input). Used by the Set field on Sealed and Card forms,
 * and by lot-pickers in multi-item forms (chunk 5c).
 *
 * The wrapper is just an <input> + <div class="combobox-list">. Open behavior:
 * the list only appears when the user types something (or hits ArrowDown to browse).
 * Arrow keys move selection, Enter accepts, Escape closes, mousedown on an item picks.
 */
import { escapeHtml } from "../lib/dom";

export interface ComboboxFieldOptions {
  label: string;
  name: string;
  value?: string;
  placeholder?: string;
  hint?: string;
}

export function comboboxField({ label, name, value = "", placeholder, hint }: ComboboxFieldOptions): string {
  const id = "f_" + name.replace(/\s+/g, "_");
  return `<div>
    <label class="label" for="${id}">${escapeHtml(label)}</label>
    <div class="combobox-wrapper" data-cbx="${escapeHtml(name)}">
      <input class="input" id="${id}" name="${name}" type="text" autocomplete="off" value="${escapeHtml(value == null ? "" : value)}" placeholder="${escapeHtml(placeholder || "Type to search…")}">
      <div class="combobox-list"></div>
    </div>
    ${hint ? `<div class="text-xs text-[var(--muted)] mt-1">${hint}</div>` : ""}
  </div>`;
}

export interface ComboboxOptions {
  getOptions: () => string[];
  onSelect?: (value: string) => void;
  asyncLoader?: () => Promise<unknown>;
}

export interface ComboboxHandle {
  refresh(): void;
  openList(): void;
  setValue(v: string): void;
  getValue(): string;
}

export function wireCombobox(wrapper: HTMLElement, opts: ComboboxOptions): ComboboxHandle {
  const input = wrapper.querySelector<HTMLInputElement>("input")!;
  const list = wrapper.querySelector<HTMLElement>(".combobox-list")!;
  let activeIdx = -1;
  let loading = false;

  const renderList = (filter: string) => {
    const all = opts.getOptions() || [];
    const f = (filter || "").toLowerCase().trim();
    const matches = (f ? all.filter(o => o.toLowerCase().includes(f)) : all).slice(0, 60);
    if (loading && !matches.length) {
      list.innerHTML = `<div class="combobox-loading">Loading…</div>`;
    } else if (!matches.length) {
      list.innerHTML = `<div class="combobox-empty">No matches — press Enter to use as a new value.</div>`;
    } else {
      list.innerHTML = matches.map((o, i) =>
        `<div class="combobox-item${i === activeIdx ? ' active' : ''}" data-value="${escapeHtml(o)}">${escapeHtml(o)}</div>`
      ).join("");
    }
    list.style.display = "block";
  };
  const hideList = () => { list.style.display = "none"; activeIdx = -1; };

  input.addEventListener("focus", () => { activeIdx = -1; });
  input.addEventListener("input", () => {
    activeIdx = -1;
    if (input.value && input.value.trim().length > 0) renderList(input.value);
    else hideList();
  });
  input.addEventListener("blur", () => { setTimeout(hideList, 180); });
  input.addEventListener("keydown", (e) => {
    const isOpen = list.style.display === "block";
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) { renderList(input.value); return; }
      const items = list.querySelectorAll<HTMLElement>(".combobox-item");
      activeIdx = Math.min(activeIdx + 1, items.length - 1);
      renderList(input.value);
      list.querySelector(".combobox-item.active")?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "ArrowUp") {
      if (!isOpen) return;
      e.preventDefault();
      activeIdx = Math.max(activeIdx - 1, 0);
      renderList(input.value);
      list.querySelector(".combobox-item.active")?.scrollIntoView({ block: "nearest" });
    } else if (e.key === "Enter") {
      const items = list.querySelectorAll<HTMLElement>(".combobox-item");
      if (isOpen && activeIdx >= 0 && items[activeIdx]) {
        e.preventDefault();
        input.value = items[activeIdx].dataset.value || "";
        hideList();
        opts.onSelect?.(input.value);
      } else { hideList(); }
    } else if (e.key === "Escape") {
      hideList();
    }
  });
  list.addEventListener("mousedown", (e) => {
    const item = (e.target as HTMLElement).closest<HTMLElement>(".combobox-item");
    if (item) {
      e.preventDefault();
      input.value = item.dataset.value || "";
      hideList();
      opts.onSelect?.(input.value);
    }
  });

  if (opts.asyncLoader) {
    loading = true;
    Promise.resolve(opts.asyncLoader()).finally(() => {
      loading = false;
      if (document.activeElement === input && list.style.display === "block") {
        renderList(input.value);
      }
    });
  }

  return {
    refresh() { if (list.style.display === "block") renderList(input.value); },
    openList() { renderList(input.value); },
    setValue(v: string) { input.value = v; },
    getValue() { return input.value; },
  };
}
